
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

export interface TeacherMatchProfile {
  id: string;
  name: string;
  code: string;
  nickname: string;
  email: string;
  tier: string;
  gender: string;
  personality: string;
  university: string;
  major: string;
  trial_lesson: string;
  teaching_class: string;
  status: string;
  subjects: Array<{ subject: string; education_stage: string; course_type: string }>;
  specialized: string[];
  teaching_styles: Array<{ teaching_style: string; level: number }>;
  branches: string[];
  certificates: Array<{ certificate_id: string; grade: string; status: string; note: string }>;
  student_proficiency: string[];
}

export interface TeacherMatchFilters {
  search: string;
  subjects: string[];
  educationStages: string[];
  courseTypes: string[];
  specialized: string[];
  teachingStyles: string[];
  styleLevels: number[];
  gender: string[];
  personality: string[];
  trialLesson: string[];
  branches: string[];
  studentProficiency: string[];
  status: string;
}

const EMPTY_FILTERS: TeacherMatchFilters = {
  search: '',
  subjects: [],
  educationStages: [],
  courseTypes: [],
  specialized: [],
  teachingStyles: [],
  styleLevels: [],
  gender: [],
  personality: [],
  trialLesson: [],
  branches: [],
  studentProficiency: [],
  status: 'ACTIVE',
};

export const useTeacherMatch = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: TeacherMatchFilters = useMemo(() => ({
    search: searchParams.get('q') || '',
    subjects: searchParams.get('subjects')?.split(',').filter(Boolean) || [],
    educationStages: searchParams.get('stages')?.split(',').filter(Boolean) || [],
    courseTypes: searchParams.get('courseTypes')?.split(',').filter(Boolean) || [],
    specialized: searchParams.get('specialized')?.split(',').filter(Boolean) || [],
    teachingStyles: searchParams.get('styles')?.split(',').filter(Boolean) || [],
    styleLevels: searchParams.get('styleLevels')?.split(',').filter(Boolean).map(Number) || [],
    gender: searchParams.get('gender')?.split(',').filter(Boolean) || [],
    personality: searchParams.get('personality')?.split(',').filter(Boolean) || [],
    trialLesson: searchParams.get('trial')?.split(',').filter(Boolean) || [],
    branches: searchParams.get('branches')?.split(',').filter(Boolean) || [],
    studentProficiency: searchParams.get('proficiency')?.split(',').filter(Boolean) || [],
    status: searchParams.get('status') || 'ACTIVE',
  }), [searchParams]);

  const updateFilters = useCallback((updates: Partial<TeacherMatchFilters>) => {
    const newFilters = { ...filters, ...updates };
    const params = new URLSearchParams();
    if (newFilters.search) params.set('q', newFilters.search);
    if (newFilters.subjects.length) params.set('subjects', newFilters.subjects.join(','));
    if (newFilters.educationStages.length) params.set('stages', newFilters.educationStages.join(','));
    if (newFilters.courseTypes.length) params.set('courseTypes', newFilters.courseTypes.join(','));
    if (newFilters.specialized.length) params.set('specialized', newFilters.specialized.join(','));
    if (newFilters.teachingStyles.length) params.set('styles', newFilters.teachingStyles.join(','));
    if (newFilters.styleLevels.length) params.set('styleLevels', newFilters.styleLevels.join(','));
    if (newFilters.gender.length) params.set('gender', newFilters.gender.join(','));
    if (newFilters.personality.length) params.set('personality', newFilters.personality.join(','));
    if (newFilters.trialLesson.length) params.set('trial', newFilters.trialLesson.join(','));
    if (newFilters.branches.length) params.set('branches', newFilters.branches.join(','));
    if (newFilters.studentProficiency.length) params.set('proficiency', newFilters.studentProficiency.join(','));
    if (newFilters.status && newFilters.status !== 'ACTIVE') params.set('status', newFilters.status);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' ||
      filters.subjects.length > 0 ||
      filters.educationStages.length > 0 ||
      filters.courseTypes.length > 0 ||
      filters.specialized.length > 0 ||
      filters.teachingStyles.length > 0 ||
      filters.styleLevels.length > 0 ||
      filters.gender.length > 0 ||
      filters.personality.length > 0 ||
      filters.trialLesson.length > 0 ||
      filters.branches.length > 0 ||
      filters.studentProficiency.length > 0 ||
      filters.status !== 'ACTIVE';
  }, [filters]);

  // Fetch all teacher data from teacher schema
  const { data: rawTeachers, isLoading, error } = useQuery({
    queryKey: ['teacher-match-data'],
    queryFn: async () => {
      const client = supabase.schema('teacher' as any);
      
      const [teachersRes, subjectsRes, specializedRes, stylesRes, branchesRes, certsRes, proficiencyRes, profilesRes] = await Promise.all([
        (client as any).from('teachers').select('*'),
        (client as any).from('teacher_subjects').select('*'),
        (client as any).from('teacher_specialized_tutorings').select('*'),
        (client as any).from('teacher_teaching_styles').select('*'),
        (client as any).from('teacher_branches').select('*'),
        (client as any).from('teacher_certificates').select('*'),
        (client as any).from('teacher_student_proficiency_suits').select('*'),
        supabase.from('teacher_profiles_view').select('teacher_code, status'),
      ]);

      if (teachersRes.error) throw teachersRes.error;

      const teachers: any[] = teachersRes.data || [];
      const subjects: any[] = subjectsRes.data || [];
      const specialized: any[] = specializedRes.data || [];
      const styles: any[] = stylesRes.data || [];
      const branches: any[] = branchesRes.data || [];
      const certs: any[] = certsRes.data || [];
      const proficiency: any[] = proficiencyRes.data || [];

      // Build a map of teacher_code -> profile status from the public view
      const profileStatusMap = new Map<string, string>();
      (profilesRes.data || []).forEach((p: any) => {
        profileStatusMap.set(p.teacher_code, p.status || '');
      });

      // Group by teacher_id
      const subjectsByTeacher = groupBy(subjects, 'teacher_id');
      const specializedByTeacher = groupBy(specialized, 'teacher_id');
      const stylesByTeacher = groupBy(styles, 'teacher_id');
      const branchesByTeacher = groupBy(branches, 'teacher_id');
      const certsByTeacher = groupBy(certs, 'teacher_id');
      const proficiencyByTeacher = groupBy(proficiency, 'teacher_id');

      return teachers.map((t: any): TeacherMatchProfile => ({
        id: t.id,
        name: t.name || '',
        code: t.code || '',
        nickname: t.nickname || '',
        email: t.email || '',
        tier: t.tier || '',
        gender: t.gender || '',
        personality: t.personality || '',
        university: t.university || '',
        major: t.major || '',
        trial_lesson: t.trial_lesson || '',
        teaching_class: t.teaching_class || '',
        status: profileStatusMap.get(t.code) || t.status || '',
        subjects: (subjectsByTeacher[t.id] || []).map((s: any) => ({
          subject: s.subject,
          education_stage: s.education_stage,
          course_type: s.course_type,
        })),
        specialized: (specializedByTeacher[t.id] || []).map((s: any) => s.specialized_tutoring),
        teaching_styles: (stylesByTeacher[t.id] || []).map((s: any) => ({
          teaching_style: s.teaching_style,
          level: s.level,
        })),
        branches: (branchesByTeacher[t.id] || []).map((b: any) => b.branch),
        certificates: (certsByTeacher[t.id] || []).map((c: any) => ({
          certificate_id: c.certificate_id,
          grade: c.grade || '',
          status: c.status || '',
          note: c.note || '',
        })),
        student_proficiency: (proficiencyByTeacher[t.id] || []).map((p: any) => p.student_proficiency),
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

  // Client-side filtering
  const filteredTeachers = useMemo(() => {
    if (!rawTeachers) return [];
    
    return rawTeachers.filter(t => {
      // Status filter (case-insensitive)
      if (filters.status && filters.status !== 'all' && t.status?.toLowerCase() !== filters.status.toLowerCase()) return false;
      
      // Text search
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (!t.name.toLowerCase().includes(q) && 
            !t.code.toLowerCase().includes(q) && 
            !t.nickname.toLowerCase().includes(q)) return false;
      }

      // Multi-select filters
      if (filters.subjects.length && !t.subjects.some(s => filters.subjects.includes(s.subject))) return false;
      if (filters.educationStages.length && !t.subjects.some(s => filters.educationStages.includes(s.education_stage))) return false;
      if (filters.courseTypes.length && !t.subjects.some(s => filters.courseTypes.includes(s.course_type))) return false;
      if (filters.specialized.length && !t.specialized.some(s => filters.specialized.includes(s))) return false;
      if (filters.teachingStyles.length && !filters.teachingStyles.every(style => t.teaching_styles.some(s => s.teaching_style === style))) return false;
      if (filters.styleLevels.length && !filters.styleLevels.every(level => t.teaching_styles.some(s => s.level === level))) return false;
      if (filters.gender.length && !filters.gender.includes(t.gender)) return false;
      if (filters.personality.length && !filters.personality.includes(t.personality)) return false;
      if (filters.trialLesson.length && !filters.trialLesson.includes(t.trial_lesson)) return false;
      if (filters.branches.length && !t.branches.some(b => filters.branches.includes(b))) return false;
      if (filters.studentProficiency.length && !t.student_proficiency.some(p => filters.studentProficiency.includes(p))) return false;

      return true;
    });
  }, [rawTeachers, filters]);

  // Compute matched attributes per teacher for highlighting
  const getMatchedAttributes = useCallback((teacher: TeacherMatchProfile) => {
    const matched: string[] = [];
    if (filters.subjects.length) {
      teacher.subjects.filter(s => filters.subjects.includes(s.subject)).forEach(s => matched.push(s.subject));
    }
    if (filters.educationStages.length) {
      teacher.subjects.filter(s => filters.educationStages.includes(s.education_stage)).forEach(s => matched.push(s.education_stage));
    }
    if (filters.specialized.length) {
      teacher.specialized.filter(s => filters.specialized.includes(s)).forEach(s => matched.push(s));
    }
    if (filters.teachingStyles.length) {
      teacher.teaching_styles.filter(s => filters.teachingStyles.includes(s.teaching_style)).forEach(s => matched.push(s.teaching_style));
    }
    if (filters.branches.length) {
      teacher.branches.filter(b => filters.branches.includes(b)).forEach(b => matched.push(b));
    }
    return matched;
  }, [filters]);

  // Extract unique filter options from data
  const filterOptions = useMemo(() => {
    if (!rawTeachers) return null;
    return {
      subjects: [...new Set(rawTeachers.flatMap(t => t.subjects.map(s => s.subject)))].sort(),
      educationStages: [...new Set(rawTeachers.flatMap(t => t.subjects.map(s => s.education_stage)))].filter(Boolean).sort(),
      courseTypes: [...new Set(rawTeachers.flatMap(t => t.subjects.map(s => s.course_type)))].filter(Boolean).sort(),
      specialized: [...new Set(rawTeachers.flatMap(t => t.specialized))].sort(),
      teachingStyles: [...new Set(rawTeachers.flatMap(t => t.teaching_styles.map(s => s.teaching_style)))].sort(),
      styleLevels: [...new Set(rawTeachers.flatMap(t => t.teaching_styles.map(s => s.level)))].sort((a, b) => a - b),
      gender: [...new Set(rawTeachers.map(t => t.gender))].filter(Boolean).sort(),
      personality: [...new Set(rawTeachers.map(t => t.personality))].filter(Boolean).sort(),
      trialLesson: [...new Set(rawTeachers.map(t => t.trial_lesson))].filter(Boolean).sort(),
      branches: [...new Set(rawTeachers.flatMap(t => t.branches))].sort(),
      studentProficiency: [...new Set(rawTeachers.flatMap(t => t.student_proficiency))].sort(),
    };
  }, [rawTeachers]);

  return {
    teachers: filteredTeachers,
    totalCount: rawTeachers?.length || 0,
    isLoading,
    error,
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    filterOptions,
    getMatchedAttributes,
  };
};

function groupBy<T>(arr: T[], key: keyof T): Record<string, T[]> {
  return arr.reduce((acc, item) => {
    const k = String(item[key]);
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {} as Record<string, T[]>);
}
